import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../utils/axiosInstance';

// Async Thunks
export const fetchPermissions = createAsyncThunk(
    'permission/fetchPermissions',
    async ({ page, limit, search }, { rejectWithValue }) => {
        try {
            const params = {
                page,
                limit,
                ...(search && { search }),
            };
            const response = await axiosInstance.get('/api/admin/permissions', { params });
            return {
                data: response.data?.data?.records || [],
                pagination: response.data?.data?.pagination || {},
            };
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch permissions');
        }
    }
);

export const createPermission = createAsyncThunk(
    'permission/createPermission',
    async (data, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post('/api/admin/permissions', data);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to create permission');
        }
    }
);

export const updatePermission = createAsyncThunk(
    'permission/updatePermission',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.patch(`/api/admin/permissions/${id}`, data);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update permission');
        }
    }
);

export const deletePermission = createAsyncThunk(
    'permission/deletePermission',
    async (id, { rejectWithValue }) => {
        try {
            await axiosInstance.delete(`/api/admin/permissions/${id}`);
            return id;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete permission');
        }
    }
);

const permissionSlice = createSlice({
    name: 'permission',
    initialState: {
        permissions: [],
        loading: false,
        error: null,
        editPermission: null, // For prefilling data
        pagination: {
            totalDocs: 0,
            totalPages: 1,
            page: 1,
            limit: 3,
        },
    },
    reducers: {
        setEditPermission: (state, action) => {
            state.editPermission = action.payload;
        },
        clearEditPermission: (state) => {
            state.editPermission = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch
            .addCase(fetchPermissions.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchPermissions.fulfilled, (state, action) => {
                state.loading = false;
                state.permissions = action.payload.data;
                state.pagination = action.payload.pagination;
            })
            .addCase(fetchPermissions.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Create
            .addCase(createPermission.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createPermission.fulfilled, (state, action) => {
                state.loading = false;
                // Optionally append the new permission if needed, but usually we refetch
            })
            .addCase(createPermission.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Update
            .addCase(updatePermission.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updatePermission.fulfilled, (state, action) => {
                state.loading = false;
                state.editPermission = null; // Clear edit state after success
            })
            .addCase(updatePermission.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Delete
            .addCase(deletePermission.fulfilled, (state, action) => {
                state.permissions = state.permissions.filter((p) => p._id !== action.payload);
            });
    },
});

export const { setEditPermission, clearEditPermission } = permissionSlice.actions;
export default permissionSlice.reducer;
